import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function BillingReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [info, setInfo] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (params.get("free") === "1") {
      setStatus("paid");
      const exp = params.get("expires");
      setInfo({ subscription: { expires_at: exp || null } });
      refresh();
      return;
    }
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      setAttempt(tries);
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (tries >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (tries >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="billing-return-page">
      <div className="ff-card p-8 md:p-10 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <div className="text-4xl">⏳</div>
            <h1 className="text-2xl font-semibold mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              Confirming your payment…
            </h1>
            <p className="text-sm text-[#5C6B61] mt-2">
              Hang on a sec — checking with Stripe (attempt {attempt}/{MAX_ATTEMPTS})
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="text-5xl">🎉</div>
            <h1 className="text-2xl font-semibold mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              Welcome to Shop, Cal & Meal Pro!
            </h1>
            <p className="text-sm text-[#5C6B61] mt-2">
              Your subscription is active.
              {info?.subscription?.expires_at && (
                <> Renews on {new Date(info.subscription.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}.</>
              )}
            </p>
            <div className="flex gap-3 mt-6 justify-center">
              <Link to="/app/planner" className="ff-btn-primary text-sm" data-testid="open-planner-btn">Open Planner</Link>
              <Link to="/app/discover" className="ff-btn-ghost text-sm border border-[#E5E2DA]" data-testid="back-to-discover-btn">Find a meal</Link>
            </div>
          </>
        )}
        {status === "expired" && (
          <>
            <div className="text-4xl">⏱️</div>
            <h1 className="text-2xl font-semibold mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              Payment session expired
            </h1>
            <p className="text-sm text-[#5C6B61] mt-2">No charge was made. You can try again whenever you&apos;re ready.</p>
            <Link to="/app/billing" className="ff-btn-primary text-sm inline-block mt-5">Back to plans</Link>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="text-4xl">😕</div>
            <h1 className="text-2xl font-semibold mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              We couldn&apos;t confirm payment
            </h1>
            <p className="text-sm text-[#5C6B61] mt-2">
              If you completed checkout, refresh in a minute — Stripe might still be processing.
              Otherwise nothing was charged.
            </p>
            <Link to="/app/billing" className="ff-btn-primary text-sm inline-block mt-5">Back to plans</Link>
          </>
        )}
      </div>
    </div>
  );
}
